import React, { useState, useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import isEmpty from 'lodash/isEmpty';
import cx from 'classnames';
import { Tabs, TabList, Tab, TabPanel } from 'react-aria-components';
import type { BlockEditProps } from '@plone/types';
import { BlocksForm, BlockDataForm } from '@plone/volto/components/manage/Form';
import SidebarPortal from '@plone/volto/components/manage/Sidebar/SidebarPortal';
import { emptyBlocksForm } from '@plone/volto/helpers/Blocks/Blocks';
import { withBlockExtensions } from '@plone/volto/helpers/Extensions';
import { type TabsData } from '@redturtle/qubicaamf-theme/components/Blocks/Tabs/schema';
import CTA from '@redturtle/qubicaamf-theme/components/Blocks/commons/CTA';
import EditBlockWrapper from '@redturtle/qubicaamf-theme/components/Blocks/Tabs/EditBlockWrapper';

import '@redturtle/qubicaamf-theme/components/Blocks/Tabs/editor.scss';
import styles from '@redturtle/qubicaamf-theme/components/Blocks/Tabs/styles.module.css';

import config from '@plone/registry';

type TabsEditProps = Omit<BlockEditProps, 'data'> & {
  data: TabsData;
};

const Edit = (props: TabsEditProps) => {
  const intl = useIntl();
  const Container = config.getComponent('Container').component || 'div';
  const [selectedBlock, setSelectedBlock] = useState({});

  const {
    block,
    data,
    blocksErrors,
    onChangeBlock,
    onChangeField,
    pathname,
    selected,
    manage,
    blocksConfig,
  } = props;

  const { tabs_style } = data;

  const metadata = props.metadata || props.properties;

  const blockState = {};

  const schema = blocksConfig[data['@type']].blockSchema({
    data,
    intl,
  });

  // it is the only way to allow the focus to go back on the whole block instead the block contained in the tab
  // at least this way it i focus on another block and then go back to the tabs block, the sidebar shows the settings
  // for the tabs block and not the blocks contained
  useEffect(() => {
    if (!selected) {
      setSelectedBlock({});
    }
  }, [selected]);

  useEffect(() => {
    // Ensure blocks for all tabs exist
    let updated = false;

    const updatedTabs = data.tabs?.map((tab) => {
      if (isEmpty(tab.blocks)) {
        updated = true;
        return {
          ...tab,
          ...emptyBlocksForm(),
        };
      }
      return tab;
    });

    if (updated) {
      onChangeBlock(block, {
        ...data,
        tabs: updatedTabs,
      });
    }
  }, [onChangeBlock, block, data]);

  const onKeyDown = (e) => {
    if (Object.keys(selectedBlock ?? {}).length === 0) {
      //nessun blocco selezionato nei contents
      props.handleKeyDown(e, props.index, props.id, props.block, {
        disableEnter: false,
        disableArrowUp: false,
        disableArrowDown: false,
      });
    }
  };

  return (
    <>
      <section
        className={cx('block-tabs', styles.block)}
        aria-label={data.title}
      >
        <Container
          className={cx('tabs-block', styles.container)}
          onKeyDown={onKeyDown}
        >
          {data?.title && (
            <div className={styles.tabs_block_header}>
              <div className="title">
                <h2>{data.title}</h2>
              </div>
            </div>
          )}

          <Tabs>
            <div className={styles.tabs_wrapper_container}>
              <TabList
                className={cx(styles.tabs_wrapper, {
                  [styles.tablist_button_style]: tabs_style === 'buttons',
                })}
              >
                {data.tabs?.map((tab) => (
                  <Tab
                    key={tab['@id']}
                    id={tab['@id']}
                    className={cx(styles.tab, {
                      [styles.tabs_simple_style]: tabs_style === 'simple',
                      [styles.tabs_button_style]: tabs_style === 'buttons',
                    })}
                  >
                    {tab.title}
                  </Tab>
                ))}
              </TabList>
            </div>
            {data.tabs?.map((tab) => {
              const panel = {
                blocks: tab.blocks,
                blocks_layout: tab.blocks_layout,
              };

              return (
                <TabPanel key={tab['@id']} id={tab['@id']}>
                  <BlocksForm
                    key={tab['@id']}
                    title={intl.formatMessage(messages.edit_placeholder)}
                    manage={manage}
                    allowedBlocks={blocksConfig[data['@type']].allowedBlocks}
                    metadata={metadata}
                    properties={isEmpty(panel) ? emptyBlocksForm() : panel}
                    selectedBlock={selected ? selectedBlock[tab['@id']] : null}
                    onSelectBlock={(id) =>
                      setSelectedBlock({
                        [tab['@id']]: id,
                      })
                    }
                    onChangeFormData={(newFormData) => {
                      const newTabs = (data.tabs ?? []).map((t) => {
                        if (t['@id'] === tab['@id']) {
                          return {
                            ...t,
                            ...newFormData,
                          };
                        }
                        return t;
                      });
                      onChangeBlock(block, {
                        ...data,
                        tabs: newTabs,
                      });
                    }}
                    onChangeField={(id, value) => {
                      if (['blocks', 'blocks_layout'].indexOf(id) > -1) {
                        blockState[id] = value;
                        const newTabs = (data.tabs ?? []).map((t) => {
                          if (t['@id'] === tab['@id']) {
                            return {
                              ...t,
                              ...blockState,
                            };
                          }
                          return t;
                        });
                        onChangeBlock(block, {
                          ...data,
                          tabs: newTabs,
                        });
                      } else {
                        onChangeField(id, value);
                      }
                    }}
                    pathname={pathname}
                  >
                    {({ draginfo }, editBlock, blockProps, ref) => {
                      return (
                        <EditBlockWrapper
                          draginfo={draginfo}
                          blockProps={blockProps}
                          extraControls={<></>}
                        >
                          {editBlock}
                        </EditBlockWrapper>
                      );
                    }}
                  </BlocksForm>
                </TabPanel>
              );
            })}
          </Tabs>

          {data.linkHref?.[0] && (
            <CTA
              href={data.linkHref[0]['@id']}
              onClick={(e: React.SyntheticEvent<HTMLLinkElement>) => {
                e.preventDefault();
              }}
              openLinkInNewTab={false}
              ctaButtonStyle={data.ctaButtonStyle}
              linkIcon={data.linkIcon}
              cta_color={data.cta_color}
            >
              {data.linkTitle}
            </CTA>
          )}
        </Container>
      </section>
      {/* @ts-ignore */}
      <SidebarPortal selected={selected && !Object.keys(selectedBlock).length}>
        <BlockDataForm
          schema={schema}
          title={schema.title}
          onChangeField={(id, value) => {
            onChangeBlock(block, {
              ...data,
              [id]: value,
            });
          }}
          onChangeBlock={onChangeBlock}
          formData={data}
          block={block}
          errors={blocksErrors}
        />
      </SidebarPortal>
    </>
  );
};

export default withBlockExtensions(Edit);

const messages = defineMessages({
  edit_placeholder: {
    id: 'edit_placeholder',
    defaultMessage: 'Enter text or choose a block',
  },
});
